import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { FC } from 'react';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import {
  BulletList,
  Card,
  CardGrid,
  PageBody,
  PageHeading,
  PageSurface,
} from '../../../design-system/components';
import { useThemeToggle } from '../ThemeProvider/WorkingThemeProvider';
import styles from './Tools.module.scss';

interface Tool {
  title: string;
  description: string;
  bullets: string[];
  icon: string;
  /** Variant whose dark elements are lifted so the mark reads on a dark ground. */
  iconDark?: string;
  /** The icon is artwork rather than a brand mark, so it fills the media slot. */
  artwork?: boolean;
  links: Array<{ label: string; url: string }>;
}

const tools: Tool[] = [
  {
    title: 'Brain Scaffold Builder',
    description:
      'Framework that allows you to create workflows to reconstruct and simulate neural network models.',
    bullets: [
      'Turn a configuration file into a reconstructed neural network',
      'Parametrize your reconstruction workflow with your own strategies',
      'Simulate your network with state-of-the-art simulators',
    ],
    icon: '/images/logo_bsb.svg',
    iconDark: '/images/logo_bsb-dark.png',
    links: [{ label: 'Documentation', url: 'https://bsb.readthedocs.io/en/latest/index.html' }],
  },
  {
    title: 'Cerebellar models',
    description: 'Code, configurations and data used to reconstruct and simulate cerebellar cortex models.',
    bullets: [
      'Reconstruct multi-species, multi-scale circuits of the cerebellum',
      'Reproduce models from the Department of Brain and Behavioral Sciences iterative work',
      "Analyse the circuits' structural properties and their results under simulation",
    ],
    icon: '/images/logo_dbbs.svg',
    iconDark: '/images/logo_dbbs-dark.svg',
    links: [
      { label: 'Circuits', url: 'https://cerebellar-models.readthedocs.io/en/latest/index.html' },
      { label: 'Neuron models', url: 'https://github.com/dbbs-lab/models' },
    ],
  },
  {
    title: 'Single cell models',
    description: 'Multi-compartmental neuron models.',
    bullets: ['Use our cell models with the NEURON simulator'],
    icon: '/images/tool_single_cell.jpg',
    artwork: true,
    links: [{ label: 'ModelDB', url: 'https://modeldb.science/modelauthor/Masoli%20S' }],
  },
  {
    title: 'The Virtual Brain',
    description: "Simulator to understand the whole brain's behaviour as a network's performance.",
    bullets: [
      'Extract critical network parameters of the human brain',
      'Analyse the influence of each node on the brain function',
      'Tweak the brain parameters to rectify a malfunctioning or damaged network',
    ],
    icon: '/images/logo_tvb_main.svg',
    iconDark: '/images/logo_tvb_main-dark.svg',
    links: [{ label: 'thevirtualbrain.org', url: 'https://www.thevirtualbrain.org/' }],
  },
];

const Tools: FC = () => {
  const navigate = useNavigate();
  const { themeMode } = useThemeToggle();

  const handleNavItemClick = (item: string) => {
    switch (item) {
      case 'Model Exploration':
        navigate('/model-exploration');
        break;
      case 'Tools':
        navigate('/tools');
        break;
      case 'Learning':
        navigate('/learning');
        break;
      case 'Contact':
        navigate('/contact');
        break;
      default:
        break;
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header pageTitle="Tools" onNavItemClick={handleNavItemClick} />

      <PageSurface fullHeight>
        <PageHeading
          compact
          title="Build, simulate, analyse"
          lede="The software the lab builds and uses. Each tool links out to its own documentation, repository or model database."
        />
        <PageBody compact>
          <CardGrid columns={4}>
            {tools.map((tool) => (
              <Card
                key={tool.title}
                title={tool.title}
                mediaAspect={2}
                media={
                  tool.artwork ? (
                    tool.icon
                  ) : (
                    <div className={styles.logo}>
                      <img src={(themeMode === 'dark' && tool.iconDark) || tool.icon} alt="" />
                    </div>
                  )
                }
                footer={tool.links.map((link) => (
                  <Button
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    className={styles.action}
                  >
                    {link.label}
                  </Button>
                ))}
              >
                <p className={styles.description}>{tool.description}</p>
                <BulletList items={tool.bullets} />
              </Card>
            ))}
          </CardGrid>
        </PageBody>
      </PageSurface>

      <Footer />
    </div>
  );
};

export default Tools;
